import {
  TextareaHTMLAttributes,
  forwardRef,
  useId,
  useEffect,
  useRef,
  useCallback,
} from 'react'

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  hint?: string
  autoResize?: boolean
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      hint,
      autoResize = true,
      className = '',
      id: providedId,
      onInput,
      ...props
    },
    ref
  ) => {
    const generatedId = useId()
    const id = providedId || generatedId
    const hasError = Boolean(error)
    const internalRef = useRef<HTMLTextAreaElement | null>(null)

    const adjustHeight = useCallback(() => {
      const textarea = internalRef.current
      if (textarea && autoResize) {
        textarea.style.height = 'auto'
        textarea.style.height = `${textarea.scrollHeight}px`
      }
    }, [autoResize])

    useEffect(() => {
      adjustHeight()
    }, [props.value, adjustHeight])

    const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
      adjustHeight()
      onInput?.(e)
    }

    const setRefs = (element: HTMLTextAreaElement | null) => {
      internalRef.current = element
      if (typeof ref === 'function') {
        ref(element)
      } else if (ref) {
        ref.current = element
      }
    }

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={id}
            className="block text-sm font-semibold text-slate-700 mb-1.5"
          >
            {label}
          </label>
        )}
        <textarea
          ref={setRefs}
          id={id}
          onInput={handleInput}
          className={`
            w-full px-3 py-2
            text-slate-900 placeholder-slate-400
            bg-white border rounded-lg
            transition-colors duration-150
            focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
            disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed
            resize-none
            ${autoResize ? 'overflow-hidden' : ''}
            ${hasError ? 'border-red-500 focus:ring-red-500' : 'border-slate-300'}
            ${className}
          `}
          aria-invalid={hasError}
          aria-describedby={
            hasError ? `${id}-error` : hint ? `${id}-hint` : undefined
          }
          {...props}
        />
        {error && (
          <p id={`${id}-error`} className="mt-1.5 text-sm text-red-600">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={`${id}-hint`} className="mt-1.5 text-sm text-slate-500">
            {hint}
          </p>
        )}
      </div>
    )
  }
)

Textarea.displayName = 'Textarea'
